/**
 * VirtualBracelet
 * Physics-based bracelet visualization using Matter.js
 * Features:
 * - Fixed 50 chain links with beads distributed evenly
 * - 2-3 fixed anchor points
 * - Draggable with realistic physics
 * - Device tilt affects gravity (mobile)
 */

import { useRef, useEffect, useState, useCallback } from 'react';
import styles from '../styles/VirtualBracelet.module.css';

let Matter = null;

// Chain links are now auto-calculated based on screen width (~1 per 8px, clamped 30–100)

function VirtualBracelet({ beads = [], className, backgroundImage, cordTexture, leftClaspTexture, rightClaspTexture, mini = false, chainLinks, onReady, animateBeadSlug, anchorYRatio = 0.25, sagRatio = 0.4 }) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const engineRef = useRef(null);
  const renderRef = useRef(null);
  const beadBodiesRef = useRef([]);
  // Tracks the slug whose drop-in animation has already played, so a scene
  // rebuild (e.g. resize, data reload) doesn't replay it.
  const animatedSlugRef = useRef(null);
  const cordTextureRef = useRef(null);
  const cordNoEndRef = useRef(null);
  const leftClaspRef = useRef(null);
  const rightClaspRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [cordTextureLoaded, setCordTextureLoaded] = useState(false);
  const [claspsLoaded, setClaspsLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [isExpanded, setIsExpanded] = useState(false);
  const loadedTexturesRef = useRef({});

  // Load Matter.js
  useEffect(() => {
    const loadMatter = async () => {
      try {
        const matterModule = await import('matter-js');
        Matter = matterModule.default || matterModule;
        setIsLoaded(true);
      } catch (err) {
        console.error('[VirtualBracelet] Failed to load Matter.js:', err);
        setError('Physics engine failed to load');
      }
    };
    loadMatter();
  }, []);

  // Load cord texture
  useEffect(() => {
    if (!cordTexture) {
      setCordTextureLoaded(true);
      return;
    }

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      cordTextureRef.current = img;
      setCordTextureLoaded(true);
      console.log('[VirtualBracelet] Cord texture loaded:', cordTexture);
    };
    img.onerror = () => {
      console.warn('[VirtualBracelet] Failed to load cord texture, using fallback');
      setCordTextureLoaded(true);
    };
    img.src = cordTexture;

    // Also load the no-end variant for segments drawn over beads
    const noEndImg = new Image();
    noEndImg.crossOrigin = 'anonymous';
    noEndImg.onload = () => {
      cordNoEndRef.current = noEndImg;
    };
    noEndImg.src = '/assets/rope-segment-noend.png';
  }, [cordTexture]);

  // Load clasp textures
  useEffect(() => {
    let loadedCount = 0;
    const totalToLoad = (leftClaspTexture ? 1 : 0) + (rightClaspTexture ? 1 : 0);

    if (totalToLoad === 0) {
      setClaspsLoaded(true);
      return;
    }

    const checkAllLoaded = () => {
      loadedCount++;
      if (loadedCount >= totalToLoad) {
        setClaspsLoaded(true);
      }
    };

    if (leftClaspTexture) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        leftClaspRef.current = img;
        console.log('[VirtualBracelet] Left clasp loaded:', leftClaspTexture);
        checkAllLoaded();
      };
      img.onerror = () => {
        console.warn('[VirtualBracelet] Failed to load left clasp');
        checkAllLoaded();
      };
      img.src = leftClaspTexture;
    }

    if (rightClaspTexture) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        rightClaspRef.current = img;
        console.log('[VirtualBracelet] Right clasp loaded:', rightClaspTexture);
        checkAllLoaded();
      };
      img.onerror = () => {
        console.warn('[VirtualBracelet] Failed to load right clasp');
        checkAllLoaded();
      };
      img.src = rightClaspTexture;
    }
  }, [leftClaspTexture, rightClaspTexture]);

  // Convert a loaded image into an offscreen canvas texture. A canvas is
  // drawable SYNCHRONOUSLY (unlike an Image built from a data URL, whose
  // async decode left beads rendering as fallback spheres for a few frames —
  // the "big white circles" flash on every rebuild). The canvas object is
  // shared by every bead body that uses the texture, so repainting it in
  // place (full-res upgrade) upgrades all of them at once.
  const imageToTexture = useCallback((img) => {
    const canvas = document.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height;
    canvas.getContext('2d').drawImage(img, 0, 0);
    return { width: img.width, height: img.height, canvas };
  }, []);

  // Preload bead images: load thumbnails first (fast), then upgrade to full-res.
  // A watchdog timeout guarantees imagesLoaded eventually flips true even if an
  // image request stalls forever (flaky network). Without it, a stalled load
  // leaves imagesLoaded=false, which tears down the physics scene and never
  // rebuilds it — freezing the bracelet in its last-painted positions.
  useEffect(() => {
    if (beads.length === 0) {
      setImagesLoaded(true);
      return;
    }

    const beadsWithImages = beads.filter(b => b.bead_image);

    if (beadsWithImages.length === 0) {
      setImagesLoaded(true);
      return;
    }

    // Textures already decoded from a previous bead set are KEPT (keyed by
    // image URL) — only genuinely new images are fetched. Wiping the cache on
    // every change re-downloaded everything and tore the scene down, which is
    // what made adding one bead flash the whole bracelet.
    const missingBeads = beadsWithImages.filter(b => !loadedTexturesRef.current[b.bead_image]);
    if (missingBeads.length === 0) {
      setImagesLoaded(true);
      return;
    }
    setImagesLoaded(false);

    // `cancelled` guards against stale callbacks from a previous beads set
    // firing after this effect has been torn down. `pendingImages` is tracked
    // so those callbacks can be detached on cleanup.
    let cancelled = false;
    const pendingImages = [];

    // Phase 1: Load thumbnails (small Strapi format) for fast initial render
    let thumbsDone = 0;
    let resolved = false;
    const textures = {};
    const thumbUrls = missingBeads.map(b => b.bead_image_thumb || b.bead_image);
    const doneFlags = new Array(thumbUrls.length).fill(false);

    // Phase 2: Upgrade to full-res in background (only if we used thumbs).
    // The upgrade repaints the SHARED texture canvas in place, so every bead
    // body referencing it upgrades without touching the physics scene.
    function startPhase2() {
      const beadsNeedingUpgrade = missingBeads.filter(b => b.bead_image_thumb && b.bead_image_thumb !== b.bead_image);
      if (beadsNeedingUpgrade.length === 0) return;
      let fullLoaded = 0;
      beadsNeedingUpgrade.forEach(bead => {
        const fullImg = new Image();
        fullImg.crossOrigin = 'anonymous';
        pendingImages.push(fullImg);
        fullImg.onload = () => {
          if (cancelled) return;
          const entry = loadedTexturesRef.current[bead.bead_image];
          if (entry?.canvas) {
            entry.canvas.width = fullImg.width;
            entry.canvas.height = fullImg.height;
            entry.canvas.getContext('2d').drawImage(fullImg, 0, 0);
            entry.width = fullImg.width;
            entry.height = fullImg.height;
          }
          fullLoaded++;
          if (fullLoaded === beadsNeedingUpgrade.length) {
            console.log('[VirtualBracelet] Full-res upgrade complete');
          }
        };
        fullImg.onerror = () => { fullLoaded++; };
        fullImg.src = bead.bead_image;
      });
    }

    const finishPhase1 = () => {
      if (cancelled || resolved) return;
      resolved = true;
      loadedTexturesRef.current = { ...loadedTexturesRef.current, ...textures };
      console.log('[VirtualBracelet] Thumbnails ready:', Object.keys(textures).length, '/', thumbUrls.length);
      setImagesLoaded(true);
      startPhase2();
    };

    // Idempotent per slot, so a late onload and the watchdog can't double-count.
    const markThumbDone = (i) => {
      if (cancelled || doneFlags[i]) return;
      doneFlags[i] = true;
      thumbsDone++;
      if (thumbsDone === thumbUrls.length) finishPhase1();
    };

    thumbUrls.forEach((url, i) => {
      const fullUrl = beadsWithImages[i].bead_image;
      const img = new Image();
      img.crossOrigin = 'anonymous';
      pendingImages.push(img);

      img.onload = () => {
        if (cancelled) return;
        textures[fullUrl] = imageToTexture(img);
        markThumbDone(i);
      };
      img.onerror = () => {
        if (cancelled) return;
        if (img.src !== fullUrl && url !== fullUrl) {
          // Thumbnail failed — retry with the full-res image before giving up.
          console.warn('[VirtualBracelet] Thumbnail failed, retrying full image:', fullUrl);
          img.src = fullUrl;
          return;
        }
        console.error('[VirtualBracelet] Image load failed:', fullUrl);
        markThumbDone(i);
      };
      img.src = url;
    });

    // Watchdog: if a request stalls with no onload/onerror, force-resolve so the
    // bracelet still renders with whatever textures arrived in time.
    const watchdog = setTimeout(() => {
      if (cancelled || resolved) return;
      console.warn('[VirtualBracelet] Image load watchdog fired —', thumbsDone, '/', thumbUrls.length, 'ready');
      finishPhase1();
    }, 8000);

    return () => {
      cancelled = true;
      clearTimeout(watchdog);
      // Detach handlers so a stalled request can't fire into a dead effect.
      pendingImages.forEach(img => { img.onload = null; img.onerror = null; });
    };
  }, [beads, imageToTexture]);

  // Update dimensions with ResizeObserver
  useEffect(() => {
    if (!containerRef.current) return;

    const updateDimensions = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          setDimensions({ width: rect.width, height: rect.height });
        }
      }
    };

    const initialTimeout = setTimeout(updateDimensions, 50);
    const resizeObserver = new ResizeObserver(updateDimensions);
    resizeObserver.observe(containerRef.current);

    return () => {
      clearTimeout(initialTimeout);
      resizeObserver.disconnect();
    };
  }, []);

  // Initialize physics
  useEffect(() => {
    if (!isLoaded || !Matter || !canvasRef.current || dimensions.width === 0 || !imagesLoaded || !cordTextureLoaded || !claspsLoaded) return;

    const { Engine, Render, World, Bodies, Composite, Composites, Constraint, Mouse, MouseConstraint, Events, Runner } = Matter;

    const engine = Engine.create({
      gravity: { x: 0, y: 0.5 }, // Lower gravity - less force
      constraintIterations: 6,
      positionIterations: 10,
      velocityIterations: 6
    });
    engineRef.current = engine;

    const render = Render.create({
      canvas: canvasRef.current,
      engine: engine,
      options: {
        width: dimensions.width,
        height: dimensions.height,
        wireframes: false,
        background: 'transparent',
        pixelRatio: window.devicePixelRatio || 1
      }
    });
    renderRef.current = render;

    // Simple chain configuration - larger, more massive
    const centerX = dimensions.width / 2;
    // Scale chain links to screen width: ~1 link per 8px of width, clamped 30–100
    const autoLinks = Math.max(30, Math.min(100, Math.round(dimensions.width / 8)));
    const numLinks = chainLinks || autoLinks;
    const linkLength = 5; // Distance between links
    const linkRadius = 8; // Larger chain links
    const isMobile = dimensions.width < 768;
    const beadRadius = isMobile ? 27 : 36; // 75% of original size

    // Anchor positions. Ratios are overridable (defaults preserve prior
    // behavior) so callers can lower the anchors to vertically center the
    // sagging bracelet in a taller container.
    const sagAmount = dimensions.height * sagRatio; // How much the chain sags below anchors
    const anchorY = dimensions.height * anchorYRatio;
    // Use 85% of width, allow up to 800px for wider screens
    const anchorSpread = Math.min(dimensions.width * 0.85, 800);
    const startX = centerX - anchorSpread / 2;

    console.log('[VirtualBracelet] Layout:', {
      containerWidth: dimensions.width,
      centerX,
      anchorSpread,
      startX,
      endX: centerX + anchorSpread / 2,
      beadCount: beads.length
    });
    const endX = centerX + anchorSpread / 2;

    // Calculate which positions will have beads
    const beadPositions = new Map();
    if (beads.length > 0) {
      const spacing = Math.floor(numLinks / (beads.length + 1));
      beads.forEach((bead, i) => {
        beadPositions.set((i + 1) * spacing, bead);
      });
    }

    // Create chain links (for physics) and beads (for display) separately
    const chainBodies = []; // Only chain links - goes in composite
    const beadBodies = [];  // Beads - added separately on top
    const beadIndices = []; // Track which indices have beads

    for (let i = 0; i < numLinks; i++) {
      const progress = i / (numLinks - 1);
      const x = startX + progress * (endX - startX);
      const sag = Math.sin(progress * Math.PI) * sagAmount;
      const y = anchorY + sag;

      const isFirst = i === 0;
      const isLast = i === numLinks - 1;
      const bead = beadPositions.get(i);

      // Always create a chain link body for physics (invisible - we draw custom cord)
      const chainBody = Bodies.circle(x, y, linkRadius, {
        isStatic: isFirst || isLast,
        density: 0.001,
        frictionAir: 0.03,
        collisionFilter: {
          group: -1 // Negative group = never collide with same group
        },
        render: {
          visible: false // Hide default rendering - we'll draw braided cord manually
        },
        label: `link-${i}`
      });
      // Known-good resting spot, used to recover the body from a physics blow-up.
      chainBody.homePosition = { x, y };
      chainBodies.push(chainBody);

      // If there's a bead at this position, create it separately
      if (bead) {
        const textureInfo = loadedTexturesRef.current[bead.bead_image];
        const renderOptions = {
          displaySize: beadRadius * 2
        };

        // Drop-in animation: a freshly-earned bead spawns above the cord and
        // animates down onto it. Guarded by a ref so it plays once per slug.
        const isAnimatingIn = !!animateBeadSlug
          && bead.bead_slug === animateBeadSlug
          && animatedSlugRef.current !== animateBeadSlug;
        const dropOffset = Math.min(240, dimensions.height * 0.35);
        const spawnY = isAnimatingIn ? y - dropOffset : y;

        const beadBody = Bodies.circle(x, spawnY, beadRadius, {
          // Static during the scripted drop, then handed back to physics.
          isStatic: isFirst || isLast || isAnimatingIn,
          density: 0.012,
          frictionAir: 0.03,
          collisionFilter: {
            group: -1 // Negative group = never collide with same group
          },
          render: {
            visible: false // Hide from default render - we'll draw manually on top
          },
          label: `bead-${bead.id || i}`
        });
        if (isAnimatingIn) {
          beadBody.dropAnim = { x, fromY: spawnY, toY: y, start: null, duration: 750 };
        }
        // Known-good resting spot (the settled y, not the drop-in spawn y).
        beadBody.homePosition = { x, y };
        // Store render info for manual drawing. The shared texture CANVAS is
        // drawable on the very first frame (no async decode) — this is what
        // killed the placeholder-sphere flash on rebuilds.
        beadBody.customRender = renderOptions;
        if (textureInfo?.canvas) {
          beadBody.customRender.image = textureInfo.canvas;
        }
        beadBody.beadData = bead;
        beadBody.isBead = true;
        beadBody.chainIndex = i; // Track which chain link this follows
        beadBody.holePosition = bead.hole_position || 'none';
        beadBodies.push(beadBody);
        beadIndices.push(i);
      }
    }

    beadBodiesRef.current = beadBodies;

    // Create chain composite with only chain links
    const chain = Composite.create();
    Composite.add(chain, chainBodies);

    // Chain them together with constraints - hide lines (they render on top of everything)
    Composites.chain(chain, 0, 0, 0, 0, {
      stiffness: 0.9,
      damping: 0.8,
      length: linkLength,
      render: {
        visible: false // Hide constraint lines - chain links themselves show the chain
      }
    });

    // Connect each bead to its corresponding chain link
    const beadConstraints = beadBodies.map((beadBody) => {
      const chainBody = chainBodies[beadBody.chainIndex];
      const constraint = Constraint.create({
        bodyA: beadBody,
        bodyB: chainBody,
        length: 0,
        // A dropping bead starts with a slack constraint so it does not yank
        // the cord upward; stiffness is restored when the drop completes.
        stiffness: beadBody.dropAnim ? 0 : 0.9,
        damping: 1, // High damping on bead connection too
        render: { visible: false }
      });
      if (beadBody.dropAnim) beadBody.dropAnim.constraint = constraint;
      return constraint;
    });

    // Walls
    const centerY = dimensions.height / 2;
    const wallThickness = 50;
    const walls = [
      Bodies.rectangle(centerX, dimensions.height + wallThickness / 2, dimensions.width, wallThickness, {
        isStatic: true, render: { visible: false }
      }),
      Bodies.rectangle(-wallThickness / 2, centerY, wallThickness, dimensions.height, {
        isStatic: true, render: { visible: false }
      }),
      Bodies.rectangle(dimensions.width + wallThickness / 2, centerY, wallThickness, dimensions.height, {
        isStatic: true, render: { visible: false }
      })
    ];

    // Add chain and walls first (renders behind)
    World.add(engine.world, [chain, ...walls]);

    // Add bead constraints
    World.add(engine.world, beadConstraints);

    // Add beads last so they render on top
    World.add(engine.world, beadBodies);

    // Mouse interaction
    const mouse = Mouse.create(canvasRef.current);

    // Fix mouse position for high DPI displays
    const pixelRatio = window.devicePixelRatio || 1;
    mouse.pixelRatio = pixelRatio;

    const mouseConstraint = MouseConstraint.create(engine, {
      mouse: mouse,
      constraint: {
        stiffness: 0.6,
        damping: 0.1,
        render: { visible: false }
      }
    });

    // Keep render in sync with mouse
    render.mouse = mouse;

    World.add(engine.world, mouseConstraint);

    // Device tilt → gravity (mobile accelerometer)
    let orientationHandler = null;
    const setupDeviceOrientation = () => {
      orientationHandler = (event) => {
        if (!engineRef.current) return;

        const gamma = event.gamma || 0; // Left-right tilt (-90 to 90)
        const beta = event.beta || 0;   // Front-back tilt (-180 to 180)

        // Map tilt to gravity - keep original strength, tilt adjusts direction
        // gamma: -90 (left) to 90 (right) → gravity.x: -0.5 to 0.5
        // beta: phone typically held at ~45°, tilt forward/back adjusts y
        const gravityX = Math.max(-0.5, Math.min(0.5, gamma / 90));
        const gravityY = Math.max(0.2, Math.min(0.8, (beta - 30) / 120 + 0.5)); // ~0.5 at normal hold

        engineRef.current.gravity.x = gravityX;
        engineRef.current.gravity.y = gravityY;
      };

      window.addEventListener('deviceorientation', orientationHandler);
      console.log('[VirtualBracelet] Device orientation enabled');
    };

    // Device motion → shake/inertia. The orientation handler only re-points
    // gravity, so tilting just slides beads sideways. This handler reacts to
    // sudden acceleration, so physically shaking the phone (in any direction,
    // including up/down) throws the beads with real inertia.
    let motionHandler = null;
    const setupDeviceMotion = () => {
      // Slow baseline, only used when a device can't report gravity-excluded
      // acceleration — we then high-pass it to recover just the shake.
      const gravBaseline = { x: 0, y: 0 };
      let baselinePrimed = false;
      const SHAKE_THRESHOLD = 2.5; // m/s² — below this is resting jitter, ignored
      const KICK = 0.22;           // velocity (px/tick) added per m/s² of shake

      motionHandler = (event) => {
        if (!engineRef.current) return;

        // Prefer gravity-excluded acceleration; fall back to the
        // gravity-inclusive reading on devices that don't provide it.
        let acc = event.acceleration;
        const hasLinear = acc && (acc.x != null || acc.y != null || acc.z != null);
        if (!hasLinear) acc = event.accelerationIncludingGravity;
        if (!acc) return;

        let ax = acc.x || 0;
        let ay = acc.y || 0;
        if (!hasLinear) {
          // Low-pass the (slow) gravity component out, leaving just the shake.
          const a = baselinePrimed ? 0.2 : 1;
          gravBaseline.x += (ax - gravBaseline.x) * a;
          gravBaseline.y += (ay - gravBaseline.y) * a;
          baselinePrimed = true;
          ax -= gravBaseline.x;
          ay -= gravBaseline.y;
        }

        if (Math.abs(ax) + Math.abs(ay) < SHAKE_THRESHOLD) return;

        // Device axes: x → screen-right, y → screen-up. The beads lag behind
        // the phone (inertia), so the pseudo-kick is opposite the motion.
        // Canvas y points down, hence the sign flip on y.
        const kickX = -ax * KICK;
        const kickY = ay * KICK;

        // Beads get the full kick; the cord gets a lighter one so it whips
        // along instead of staying rigid. The beforeUpdate velocity clamp
        // keeps a vigorous shake from exploding the scene.
        beadBodies.forEach(body => {
          if (body.isStatic) return;
          Matter.Body.setVelocity(body, {
            x: body.velocity.x + kickX,
            y: body.velocity.y + kickY
          });
        });
        chainBodies.forEach(body => {
          if (body.isStatic) return;
          Matter.Body.setVelocity(body, {
            x: body.velocity.x + kickX * 0.4,
            y: body.velocity.y + kickY * 0.4
          });
        });
      };

      window.addEventListener('devicemotion', motionHandler);
      console.log('[VirtualBracelet] Device motion (shake) enabled');
    };

    // iOS 13+ requires a user gesture to grant orientation/motion access;
    // other platforms expose the events with no permission step.
    let firstTouchHandler = null;
    let firstTouchTarget = null;
    const needsOrientationPermission = typeof DeviceOrientationEvent !== 'undefined' &&
      typeof DeviceOrientationEvent.requestPermission === 'function';
    const needsMotionPermission = typeof DeviceMotionEvent !== 'undefined' &&
      typeof DeviceMotionEvent.requestPermission === 'function';

    if (needsOrientationPermission || needsMotionPermission) {
      // iOS - request both permissions on first canvas interaction.
      const requestPermissions = async () => {
        try {
          if (needsOrientationPermission) {
            const p = await DeviceOrientationEvent.requestPermission();
            if (p === 'granted') setupDeviceOrientation();
          } else if (typeof window.DeviceOrientationEvent !== 'undefined') {
            setupDeviceOrientation();
          }
          if (needsMotionPermission) {
            const p = await DeviceMotionEvent.requestPermission();
            if (p === 'granted') setupDeviceMotion();
          } else if (typeof window.DeviceMotionEvent !== 'undefined') {
            setupDeviceMotion();
          }
        } catch (err) {
          console.log('[VirtualBracelet] Motion permission denied:', err);
        }
      };
      // Capture the canvas node now so the cleanup detaches from the same
      // element rather than reading canvasRef.current (which may have changed).
      firstTouchTarget = canvasRef.current;
      firstTouchHandler = () => {
        requestPermissions();
        firstTouchTarget?.removeEventListener('touchstart', firstTouchHandler);
        firstTouchHandler = null;
      };
      firstTouchTarget?.addEventListener('touchstart', firstTouchHandler, { once: true });
    } else {
      // Android and other platforms - no permission needed
      if (typeof window.DeviceOrientationEvent !== 'undefined') setupDeviceOrientation();
      if (typeof window.DeviceMotionEvent !== 'undefined') setupDeviceMotion();
    }

    // Limit per-frame velocity and recover from physics blow-ups. Aggressive
    // dragging can stress the stiff constraint network until a body's position
    // or velocity goes non-finite (NaN/Infinity); once that happens Matter never
    // recovers and the body renders frozen at a garbage coordinate. We detect
    // the non-finite state and snap the body back to its home position. We also
    // clamp angular velocity, which the original clamp left unbounded.
    const maxVelocity = 12;
    const maxAngularVelocity = 0.5;
    const isFinitePoint = (p) => p && Number.isFinite(p.x) && Number.isFinite(p.y);
    Events.on(engine, 'beforeUpdate', () => {
      const allBodies = [...chainBodies, ...beadBodies];
      allBodies.forEach(body => {
        // Recover any body whose physics state has gone non-finite.
        if (!isFinitePoint(body.position) ||
            !isFinitePoint(body.velocity) ||
            !Number.isFinite(body.angularVelocity)) {
          const home = body.homePosition || { x: 0, y: 0 };
          Matter.Body.setPosition(body, { x: home.x, y: home.y });
          Matter.Body.setVelocity(body, { x: 0, y: 0 });
          Matter.Body.setAngularVelocity(body, 0);
          Matter.Body.setAngle(body, 0);
          return;
        }
        if (!body.isStatic) {
          const vx = Math.max(-maxVelocity, Math.min(maxVelocity, body.velocity.x));
          const vy = Math.max(-maxVelocity, Math.min(maxVelocity, body.velocity.y));
          Matter.Body.setVelocity(body, { x: vx, y: vy });
          const av = Math.max(-maxAngularVelocity, Math.min(maxAngularVelocity, body.angularVelocity));
          if (av !== body.angularVelocity) Matter.Body.setAngularVelocity(body, av);
        }
      });
    });

    // Drop-in animation: script a freshly-earned bead from above onto the cord,
    // then hand it back to physics and re-engage its constraint.
    Events.on(engine, 'beforeUpdate', () => {
      const now = performance.now();
      beadBodies.forEach(body => {
        const anim = body.dropAnim;
        if (!anim) return;
        if (anim.start === null) anim.start = now;
        const t = Math.min(1, (now - anim.start) / anim.duration);
        const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
        Matter.Body.setPosition(body, {
          x: anim.x,
          y: anim.fromY + (anim.toY - anim.fromY) * eased
        });
        if (t >= 1) {
          Matter.Body.setStatic(body, false);
          if (anim.constraint) anim.constraint.stiffness = 0.9;
          animatedSlugRef.current = body.beadData?.bead_slug || null;
          delete body.dropAnim;
        }
      });
    });

    // Custom render cord and beads
    const cordHeight = 16; // Desired cord thickness on screen

    Events.on(render, 'afterRender', () => {
      const ctx = render.context;
      const cordImg = cordTextureRef.current; // Read ref inside callback

      // Draw cord using sprite texture or fallback
      if (chainBodies.length > 1) {
        ctx.save();

        if (cordImg && cordImg.complete && cordImg.naturalWidth > 0) {
          // Debug: log once
          if (!cordImg._logged) {
            console.log('[VirtualBracelet] Drawing cord with texture:', cordImg.src, cordImg.naturalWidth, 'x', cordImg.naturalHeight);
            cordImg._logged = true;
          }
          // Sprite-based rope rendering
          const scale = cordHeight / cordImg.height;
          const segmentWidth = cordImg.width * scale;

          for (let i = 0; i < chainBodies.length - 1; i++) {
            const p1 = chainBodies[i].position;
            const p2 = chainBodies[i + 1].position;
            const dx = p2.x - p1.x;
            const dy = p2.y - p1.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            const angle = Math.atan2(dy, dx);

            // Draw sprite segments to cover the distance between links
            const numSegments = Math.ceil(distance / segmentWidth) + 1;
            const stepX = dx / numSegments;
            const stepY = dy / numSegments;

            for (let s = 0; s < numSegments; s++) {
              const x = p1.x + stepX * s;
              const y = p1.y + stepY * s;

              ctx.save();
              ctx.translate(x, y);
              ctx.rotate(angle);
              ctx.drawImage(
                cordImg,
                -segmentWidth / 2,
                -cordHeight / 2,
                segmentWidth,
                cordHeight
              );
              ctx.restore();
            }
          }
        } else {
          // Fallback: simple gradient cord
          ctx.beginPath();
          ctx.moveTo(chainBodies[0].position.x, chainBodies[0].position.y);
          for (let i = 1; i < chainBodies.length; i++) {
            ctx.lineTo(chainBodies[i].position.x, chainBodies[i].position.y);
          }
          ctx.strokeStyle = '#1a3d38';
          ctx.lineWidth = cordHeight;
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';
          ctx.stroke();

          // Highlight
          ctx.beginPath();
          ctx.moveTo(chainBodies[0].position.x, chainBodies[0].position.y - 2);
          for (let i = 1; i < chainBodies.length; i++) {
            ctx.lineTo(chainBodies[i].position.x, chainBodies[i].position.y - 2);
          }
          ctx.strokeStyle = '#2d5a52';
          ctx.lineWidth = cordHeight - 6;
          ctx.stroke();
        }

        ctx.restore();
      }

      // Draw decorative end pieces (clasps)
      const leftClaspImg = leftClaspRef.current;
      const rightClaspImg = rightClaspRef.current;
      const claspHeight = 38; // Desired clasp height on screen

      [chainBodies[0], chainBodies[chainBodies.length - 1]].forEach((body, idx) => {
        const x = body.position.x;
        const y = body.position.y;
        const isLeft = idx === 0;

        ctx.save();
        ctx.translate(x, y);

        // Calculate angle from clasp to next chain link for proper orientation
        const nextIdx = isLeft ? 1 : chainBodies.length - 2;
        const nextBody = chainBodies[nextIdx];
        const rawAngle = Math.atan2(nextBody.position.y - y, nextBody.position.x - x);
        // Left clasp image faces right, right clasp image faces left (already oriented)
        // For right clasp, subtract π to avoid flipping the pre-oriented image
        const angle = isLeft ? rawAngle : rawAngle - Math.PI;
        ctx.rotate(angle);

        const claspImg = isLeft ? leftClaspImg : rightClaspImg;

        if (claspImg && claspImg.complete && claspImg.naturalWidth > 0) {
          // Sprite-based clasp rendering
          const scale = claspHeight / claspImg.height;
          const w = claspImg.width * scale;
          const h = claspHeight;

          // Left clasp: centered on right edge, so draw from -width to 0
          // Right clasp: centered on left edge, so draw from 0 to width
          const drawX = isLeft ? -w : 0;

          ctx.drawImage(claspImg, drawX, -h / 2, w, h);
        } else {
          // Fallback: simple metallic circle
          ctx.beginPath();
          ctx.arc(0, 0, 14, 0, Math.PI * 2);
          const claspGradient = ctx.createRadialGradient(-3, -3, 0, 0, 0, 14);
          claspGradient.addColorStop(0, '#D4AF37');
          claspGradient.addColorStop(0.5, '#8B7355');
          claspGradient.addColorStop(1, '#3D2914');
          ctx.fillStyle = claspGradient;
          ctx.fill();
        }

        ctx.restore();
      });

      // Helper: calculate chain angle at a bead's position
      function getChainAngle(chainIdx) {
        if (chainIdx > 0 && chainIdx < chainBodies.length - 1) {
          const prev = chainBodies[chainIdx - 1].position;
          const next = chainBodies[chainIdx + 1].position;
          return Math.atan2(next.y - prev.y, next.x - prev.x);
        } else if (chainIdx === 0 && chainBodies.length > 1) {
          const curr = chainBodies[0].position;
          const next = chainBodies[1].position;
          return Math.atan2(next.y - curr.y, next.x - curr.x);
        } else if (chainIdx === chainBodies.length - 1 && chainBodies.length > 1) {
          const prev = chainBodies[chainIdx - 1].position;
          const curr = chainBodies[chainIdx].position;
          return Math.atan2(curr.y - prev.y, curr.x - prev.x);
        }
        return 0;
      }

      // Helper: draw a single bead
      function drawBead(body) {
        const pos = body.position;
        const chainAngle = getChainAngle(body.chainIndex);

        ctx.save();
        ctx.translate(pos.x, pos.y);
        ctx.rotate(chainAngle);

        const img = body.customRender?.image;
        const size = body.customRender?.displaySize;
        // Texture may be an offscreen canvas (always ready) or an Image
        const textureReady = img && (
          typeof HTMLCanvasElement !== 'undefined' && img instanceof HTMLCanvasElement
            ? img.width > 0
            : (img.complete && img.naturalWidth > 0)
        );
        if (textureReady && size) {
          ctx.drawImage(img, -size / 2, -size / 2, size, size);
        } else if (size) {
          // Fallback: a bead must NEVER be invisible. If its texture didn't
          // make it (load race, CORS, missing art), draw a simple metallic
          // sphere so the bracelet still reads correctly.
          const r = size / 2;
          const grad = ctx.createRadialGradient(-r * 0.3, -r * 0.3, r * 0.1, 0, 0, r);
          grad.addColorStop(0, '#f4f4f4');
          grad.addColorStop(0.5, '#c0c0c0');
          grad.addColorStop(1, '#8a8a8a');
          ctx.beginPath();
          ctx.arc(0, 0, r, 0, Math.PI * 2);
          ctx.fillStyle = grad;
          ctx.fill();
        }

        ctx.restore();
      }

      // Helper: draw a cord segment between two chain body indices
      const cordNoEndImg = cordNoEndRef.current;
      function drawCordSegment(fromIdx, toIdx, useNoEnd = false) {
        const p1 = chainBodies[fromIdx].position;
        const p2 = chainBodies[toIdx].position;
        const dx = p2.x - p1.x;
        const dy = p2.y - p1.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const angle = Math.atan2(dy, dx);

        const img = useNoEnd && cordNoEndImg?.complete ? cordNoEndImg : cordImg;

        if (img && img.complete && img.naturalWidth > 0) {
          const scale = cordHeight / img.height;
          const segW = img.width * scale;
          const numSegs = Math.ceil(distance / segW) + 1;
          const stepX = dx / numSegs;
          const stepY = dy / numSegs;
          for (let s = 0; s < numSegs; s++) {
            ctx.save();
            ctx.translate(p1.x + stepX * s, p1.y + stepY * s);
            ctx.rotate(angle);
            ctx.drawImage(img, -segW / 2, -cordHeight / 2, segW, cordHeight);
            ctx.restore();
          }
        } else {
          ctx.beginPath();
          ctx.moveTo(p1.x, p1.y);
          ctx.lineTo(p2.x, p2.y);
          ctx.strokeStyle = '#1a3d38';
          ctx.lineWidth = cordHeight;
          ctx.lineCap = 'round';
          ctx.stroke();
        }
      }

      // Draw all beads on top of cord
      beadBodies.forEach(body => {
        drawBead(body);
      });

      // Redraw the 2 closest chain segments on the hole side, on top of the bead
      beadBodies.forEach(body => {
        const hole = body.holePosition;
        if (hole === 'none') return;
        const idx = body.chainIndex;

        if (hole === 'left' || hole === 'both') {
          // center, no, no, zmax
          if (idx - 3 >= 0) drawCordSegment(idx - 3, idx - 2, true);
        }
        if (hole === 'right' || hole === 'both') {
          // center, no, no, zmax
          if (idx + 3 < chainBodies.length) drawCordSegment(idx + 2, idx + 3, true);
        }
      });
    });

    const runner = Runner.create();
    Runner.run(runner, engine);
    Render.run(render);

    // A freshly-earned bead is dropping in — give the rest of the bracelet a
    // soft upward ripple so the re-spaced beads visibly shuffle and settle
    // into their new spots instead of teleporting between rebuilds.
    if (beadBodies.some(b => b.dropAnim)) {
      beadBodies.forEach(b => {
        if (b.isStatic || b.dropAnim) return;
        Matter.Body.setVelocity(b, {
          x: (Math.random() - 0.5) * 4,
          y: -(1 + Math.random() * 1.5)
        });
      });
      chainBodies.forEach(b => {
        if (b.isStatic) return;
        Matter.Body.setVelocity(b, {
          x: (Math.random() - 0.5) * 1.5,
          y: -(Math.random() * 1.2)
        });
      });
    }

    // Signal ready after physics is running and first frame renders
    const readyTimer = setTimeout(() => {
      setIsExpanded(true);
      if (onReady) onReady();
    }, 100);

    return () => {
      clearTimeout(readyTimer);
      if (orientationHandler) {
        window.removeEventListener('deviceorientation', orientationHandler);
      }
      if (motionHandler) {
        window.removeEventListener('devicemotion', motionHandler);
      }
      if (firstTouchHandler && firstTouchTarget) {
        firstTouchTarget.removeEventListener('touchstart', firstTouchHandler);
      }
      Render.stop(render);
      Runner.stop(runner);
      World.clear(engine.world);
      Engine.clear(engine);
      render.canvas = null;
      render.context = null;
      render.textures = {};
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, beads, dimensions, imagesLoaded, cordTextureLoaded, claspsLoaded, animateBeadSlug]);

  // Shake - very gentle nudge
  const handleShake = useCallback(() => {
    if (!Matter || beadBodiesRef.current.length === 0) return;

    beadBodiesRef.current.forEach(body => {
      Matter.Body.setVelocity(body, {
        x: (Math.random() - 0.5) * 3,
        y: (Math.random() - 0.5) * 3
      });
    });
  }, []);

  // Note: isExpanded is set in the physics useEffect after render starts

  if (error) {
    return (
      <div className={`${styles.container} ${className || ''}`}>
        <div className={styles.error}>{error}</div>
      </div>
    );
  }

  const allLoaded = isLoaded && imagesLoaded && cordTextureLoaded && claspsLoaded;

  if (beads.length === 0 && allLoaded) {
    return (
      <div className={`${styles.container} ${className || ''}`} ref={containerRef}>
        {backgroundImage && (
          <img src={backgroundImage} alt="" className={styles.backgroundImage} />
        )}
        {!mini && (
          <div className={styles.emptyState}>
            <p className={styles.emptyText}>No beads yet</p>
            <p className={styles.emptySubtext}>Complete achievements to earn your first bead</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`${styles.container} ${className || ''} ${isExpanded ? styles.expanded : styles.collapsed}`} ref={containerRef}>
      {backgroundImage && (
        <img
          src={backgroundImage}
          alt=""
          className={styles.backgroundImage}
        />
      )}
      <canvas
        ref={canvasRef}
        className={styles.canvas}
      />
      {!mini && (
        <button className={styles.shakeButton} onClick={handleShake} title="Shake" aria-label="Shake beads">
          <svg className={styles.shakeIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M16 3h5v5M8 3H3v5M3 16v5h5M21 16v5h-5M12 8v8M8 12h8"/>
          </svg>
        </button>
      )}
      {!mini && <div className={styles.hint}>Drag beads to play</div>}
    </div>
  );
}

export default VirtualBracelet;
